/**
 * OneSignal Web Push Notification integration.
 *
 * All calls are guarded so OneSignal failures never break the app.
 * The SDK script is only loaded on production domains (see index.html).
 */

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: any) => Promise<void>>
  }
}

const APP_ID = '9ef0131f-4bd4-4278-b742-579ce72d387b'

let sdkReady = false
let sdkInstance: any = null

function isProductionDomain(): boolean {
  return window.location.hostname === 'omnitrace.app' ||
         window.location.hostname.endsWith('.omnitrace.app')
}

/**
 * Initialize OneSignal. Call once at app startup.
 * Waits for init to succeed before marking SDK as ready.
 */
export function initOneSignal(): void {
  if (!isProductionDomain()) return

  window.OneSignalDeferred = window.OneSignalDeferred || []
  window.OneSignalDeferred.push(async (OneSignal: any) => {
    try {
      await OneSignal.init({ appId: APP_ID })
      sdkInstance = OneSignal
      sdkReady = true
    } catch {
      // Init failed (e.g. service worker missing) — all subsequent calls will no-op
      sdkReady = false
    }
  })
}

/**
 * Run a function against the OneSignal SDK, only if init succeeded.
 */
async function safeRun(fn: (os: any) => Promise<void>): Promise<void> {
  if (!sdkReady || !sdkInstance) return
  try {
    await fn(sdkInstance)
  } catch {
    // Swallow — OneSignal errors must never break the app
  }
}

/**
 * Login the user to OneSignal using their OmniTrace user ID.
 */
export function loginOneSignal(
  userId: string,
  tenantId: string,
  roles?: string[],
): void {
  safeRun(async (os) => {
    await os.login(userId)
    os.User?.addTags?.({
      tenant_id: tenantId,
      roles: (roles ?? []).join(','),
    })
  })
}

/**
 * Logout from OneSignal.
 */
export function logoutOneSignal(): void {
  safeRun(async (os) => {
    await os.logout()
  })
}

/**
 * Prompt the user for push notification permission.
 */
export function requestPushPermission(): void {
  safeRun(async (os) => {
    await os.Notifications?.requestPermission()
  })
}
